// SPDX-License-Identifier: AGPL-3.0-or-later

import {HALLOWEEN_THEME_PRESET} from '@app/features/holiday/packs/halloween/themePreset';
import {
	HOLIDAY_PACK_IDS,
	type HolidayPackId,
	isHolidayThemePresetEnabled,
} from '@app/features/holiday/state/HolidayPreferences';
import {enabledHolidaySeasonPhase} from '@app/features/holiday/utils/holidaySeason';
import {Logger} from '@app/features/platform/utils/AppLogger';
import {getFluxerDebugObject} from '@app/features/platform/utils/FluxerDebugGlobal';
import {makeAutoObservable} from 'mobx';

const logger = new Logger('HolidayThemePresets');

export interface HolidayThemePreset {
	packId: HolidayPackId;
	/** Untranslated. Used as the theme name when forked into Theme Studio. */
	name: string;
	/** Complete palette. Custom properties only — no asset references, no `url()`. */
	css: string;
}

export const HOLIDAY_THEME_PRESETS: Partial<Record<HolidayPackId, HolidayThemePreset>> = {
	halloween: HALLOWEEN_THEME_PRESET,
};

/**
 * Tokens a preset must declare to be safe over any base theme.
 *
 * A preset applies on top of whichever theme the user picked, so one that sets surfaces but not
 * text leaves a Light-theme user with near-black text on black. Enforced by
 * `HolidayThemePresets.test.ts`.
 */
export const REQUIRED_HOLIDAY_PRESET_TOKENS: ReadonlyArray<string> = [
	'--background-primary',
	'--background-secondary',
	'--background-secondary-lighter',
	'--background-secondary-alt',
	'--background-tertiary',
	'--background-channel-header',
	'--background-header-primary',
	'--background-header-primary-hover',
	'--background-header-secondary',
	'--background-textarea',
	'--guild-list-foreground',
	'--text-primary',
	'--text-primary-muted',
	'--text-secondary',
	'--text-tertiary',
	'--text-tertiary-secondary',
	'--text-tertiary-muted',
	'--text-chat',
	'--text-chat-muted',
];

/**
 * DEV-only. Set to a pack id to apply that pack's preset regardless of calendar or preference.
 * Production builds never force.
 *
 * For iterating on a palette prefer the runtime override, which needs no rebuild — from the
 * console: `__fluxerHolidayTheme('halloween')` to force, `__fluxerHolidayTheme(null)` to clear.
 */
export const DEBUG_FORCE_HOLIDAY_THEME_PRESET: HolidayPackId | null = null;

class HolidayThemePresetDebugState {
	forcedPackId: HolidayPackId | null = import.meta.env.DEV ? DEBUG_FORCE_HOLIDAY_THEME_PRESET : null;

	constructor() {
		makeAutoObservable(this, {}, {autoBind: true});
	}

	setForcedPackId(packId: HolidayPackId | null): void {
		if (!import.meta.env.DEV) return;
		this.forcedPackId = packId;
	}
}

export const HolidayThemePresetDebug = new HolidayThemePresetDebugState();

export function getHolidayThemePreset(packId: HolidayPackId): HolidayThemePreset | null {
	return HOLIDAY_THEME_PRESETS[packId] ?? null;
}

export function hasHolidayThemePreset(packId: HolidayPackId): boolean {
	return getHolidayThemePreset(packId) != null;
}

/**
 * Preset to apply right now, or `null`.
 *
 * Peak only: a full palette takeover during Halloween's September preseason is not what
 * "preseason events" opted into.
 */
export function resolveActiveHolidayThemePreset(now: Date = new Date()): HolidayThemePreset | null {
	const forcedPackId = HolidayThemePresetDebug.forcedPackId;
	if (import.meta.env.DEV && forcedPackId != null) {
		return getHolidayThemePreset(forcedPackId);
	}
	for (const packId of HOLIDAY_PACK_IDS) {
		const preset = getHolidayThemePreset(packId);
		if (preset == null) continue;
		if (!isHolidayThemePresetEnabled(packId)) continue;
		if (enabledHolidaySeasonPhase(packId, now) !== 'peak') continue;
		return preset;
	}
	return null;
}

/**
 * Console handles for flipping presets without a rebuild. No-op outside DEV.
 *
 * `__FLUXER_DEBUG__.setHolidayThemePreset('halloween')` forces a preset on regardless of the
 * calendar or the user's toggle; `…(null)` returns to normal resolution.
 */
export function installHolidayThemePresetDebugApi(): void {
	if (!import.meta.env.DEV) return;
	const debugObject = getFluxerDebugObject();
	if (!debugObject) return;
	try {
		debugObject.setHolidayThemePreset = (packId: HolidayPackId | null) => {
			HolidayThemePresetDebug.setForcedPackId(packId);
			return HolidayThemePresetDebug.forcedPackId;
		};
		debugObject.getHolidayThemePreset = () => HolidayThemePresetDebug.forcedPackId;
		debugObject.listHolidayThemePresets = () => Object.keys(HOLIDAY_THEME_PRESETS);
	} catch (error) {
		logger.warn('Failed to install __FLUXER_DEBUG__ holiday theme preset helpers', error);
	}
}
